import { useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { MdCheckCircle, MdErrorOutline, MdSend } from "react-icons/md";
import { getApiErrorMessage } from "@/hooks/useApiErrorToast";
import {
  CANAL_LABEL,
  CanalComunicacao,
  DisparoConfiguracao,
  PROVEDOR_LABEL,
  ResultadoTesteDisparo,
} from "@/models/consignado/disparo.model";
import { testarDisparo } from "@/services/consignado/disparo.service";
import { fieldFont } from "./styleTokens";

type Props = {
  /** O disparo como acabou de ser salvo (inativo) — é dele que saem os canais e o id. */
  disparo: DisparoConfiguracao;
  /** A identificação pediu ativo: o Confirmar ativa o disparo. */
  ativaAoConfirmar: boolean;
  /** A ativação do Confirmar está em andamento. */
  confirmando?: boolean;
  onConfirmar: () => void;
  /** Fecha a modal e devolve ao formulário, que já está em edição. */
  onEditar: () => void;
};

const ROTULO_DESTINO: Record<CanalComunicacao, string> = {
  [CanalComunicacao.EMAIL]: "E-mail",
  [CanalComunicacao.SMS]: "Celular para o SMS",
  [CanalComunicacao.WHATSAPP]: "Número do WhatsApp",
};

const PLACEHOLDER: Record<CanalComunicacao, string> = {
  [CanalComunicacao.EMAIL]: "voce@granatech.com.br",
  [CanalComunicacao.SMS]: "(11) 99999-9999",
  [CanalComunicacao.WHATSAPP]: "(11) 99999-9999",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// O telefone vai com o 55 na frente: a Zenvia completa sozinha, mas a Meta
// envia o número como recebe, e "11 9…" sem o país não chega ao Brasil.
const telefoneComPais = (valor: string): string | null => {
  const digitos = valor.replace(/\D/g, "");
  if (digitos.length === 10 || digitos.length === 11) return `55${digitos}`;
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith("55")) return digitos;
  return null;
};

const normalizar = (canal: CanalComunicacao, valor: string): string =>
  canal === CanalComunicacao.EMAIL ? valor.trim() : telefoneComPais(valor) ?? "";

const erroDoDestino = (canal: CanalComunicacao, valor: string): string | undefined => {
  if (!valor.trim()) return "Informe para onde enviar.";
  if (canal === CanalComunicacao.EMAIL) {
    return EMAIL_REGEX.test(valor.trim()) ? undefined : "E-mail inválido.";
  }
  return telefoneComPais(valor) ? undefined : "Informe DDD + número.";
};

// Quem testa costuma mandar sempre para o próprio celular e e-mail: lembrar o
// último destino de cada canal poupa redigitar a cada rodada.
const CHAVE_DESTINOS = "disparos.teste.destinos";

const lerDestinosSalvos = (): Partial<Record<CanalComunicacao, string>> => {
  try {
    return JSON.parse(localStorage.getItem(CHAVE_DESTINOS) ?? "{}") ?? {};
  } catch {
    return {};
  }
};

const gravarDestinos = (destinos: Partial<Record<CanalComunicacao, string>>) => {
  try {
    localStorage.setItem(CHAVE_DESTINOS, JSON.stringify({ ...lerDestinosSalvos(), ...destinos }));
  } catch {
    // Sem armazenamento só se perde a sugestão da próxima vez.
  }
};

// Um backend sem o modo teste ignora o corpo e cai na execução manual, que
// recusa o disparo inativo — e o "Testar template" sempre salva inativo.
const mensagemDeErro = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const mensagem = getApiErrorMessage(error, "Não foi possível enviar o teste.");
    return /Disparo inativo/i.test(mensagem)
      ? "Este ambiente ainda não tem o envio de teste. Nada foi enviado."
      : mensagem;
  }
  return error instanceof Error ? error.message : "Não foi possível enviar o teste.";
};

export default function TesteDisparoModal({
  disparo,
  ativaAoConfirmar,
  confirmando = false,
  onConfirmar,
  onEditar,
}: Props) {
  const canais = disparo.canais;
  const [destinos, setDestinos] = useState<Partial<Record<CanalComunicacao, string>>>(() => {
    const salvos = lerDestinosSalvos();
    return Object.fromEntries(canais.map((c) => [c.canal, salvos[c.canal] ?? ""]));
  });
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [resultado, setResultado] = useState<ResultadoTesteDisparo | null>(null);

  const enviar = useMutation({
    mutationFn: () =>
      testarDisparo(disparo.id, {
        teste: {
          destinos: Object.fromEntries(
            canais.map((c) => [c.canal, normalizar(c.canal, destinos[c.canal] ?? "")]),
          ),
        },
      }),
    onSuccess: (r) => {
      gravarDestinos(destinos);
      setResultado(r);
    },
  });

  const erros = Object.fromEntries(
    canais.map((c) => [c.canal, erroDoDestino(c.canal, destinos[c.canal] ?? "")]),
  ) as Partial<Record<CanalComunicacao, string | undefined>>;
  const valido = canais.every((c) => !erros[c.canal]);

  const handleEnviar = () => {
    setTentouEnviar(true);
    if (!valido || enviar.isPending) return;
    enviar.mutate();
  };

  const algumEnviado = resultado?.canais.some((r) => r.ok) ?? false;
  const ocupado = enviar.isPending || confirmando;

  return (
    <Modal
      isOpen
      onClose={onEditar}
      isCentered
      size="lg"
      closeOnOverlayClick={false}
      closeOnEsc={!ocupado}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader {...fieldFont}>Testar disparo</ModalHeader>
        <ModalCloseButton isDisabled={ocupado} />

        {!resultado ? (
          <>
            <ModalBody>
              <Text fontSize="sm" color="gray.700" mb={1} {...fieldFont}>
                <b>{disparo.nome}</b> vai só para os destinos abaixo.
              </Text>
              <Flex gap={2} wrap="wrap" mb={2}>
                <Tag size="sm" colorScheme="purple" {...fieldFont}>
                  Variáveis com valores de exemplo
                </Tag>
                <Tag size="sm" colorScheme="gray" {...fieldFont}>
                  Ninguém do público recebe
                </Tag>
              </Flex>
              <Text fontSize="xs" color="gray.500" mb={5} {...fieldFont}>
                O disparo foi salvo inativo e nada roda para o público até você confirmar.
                {ativaAoConfirmar && " Ao confirmar, ele é ativado, como pede a identificação."}
              </Text>

              <Stack spacing={4}>
                {canais.map((c) => (
                  <FormControl key={c.canal} isInvalid={tentouEnviar && !!erros[c.canal]}>
                    <FormLabel fontSize="sm" mb={1} {...fieldFont}>
                      {ROTULO_DESTINO[c.canal]}{" "}
                      <Text as="span" color="gray.500" fontWeight="normal">
                        · pelo {PROVEDOR_LABEL[c.provedor]}
                      </Text>
                    </FormLabel>
                    <Input
                      type={c.canal === CanalComunicacao.EMAIL ? "email" : "tel"}
                      inputMode={c.canal === CanalComunicacao.EMAIL ? "email" : "tel"}
                      placeholder={PLACEHOLDER[c.canal]}
                      value={destinos[c.canal] ?? ""}
                      onChange={(e) => setDestinos((prev) => ({ ...prev, [c.canal]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && handleEnviar()}
                      {...fieldFont}
                    />
                    {c.canal !== CanalComunicacao.EMAIL && !(tentouEnviar && erros[c.canal]) && (
                      <FormHelperText fontSize="xs" {...fieldFont}>
                        DDD + número. O 55 do Brasil entra sozinho.
                      </FormHelperText>
                    )}
                    <FormErrorMessage fontSize="xs" {...fieldFont}>
                      {erros[c.canal]}
                    </FormErrorMessage>
                  </FormControl>
                ))}
              </Stack>

              {enviar.isError && (
                <Alert status="error" borderRadius="md" mt={5} fontSize="sm">
                  <AlertIcon />
                  <AlertDescription {...fieldFont}>{mensagemDeErro(enviar.error)}</AlertDescription>
                </Alert>
              )}
            </ModalBody>
            <ModalFooter gap={3}>
              <Button variant="ghost" onClick={onEditar} isDisabled={enviar.isPending} {...fieldFont}>
                Cancelar
              </Button>
              <Button
                colorScheme="green"
                leftIcon={<MdSend />}
                onClick={handleEnviar}
                isLoading={enviar.isPending}
                loadingText="Enviando"
                {...fieldFont}
              >
                Enviar teste
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalBody>
              <Stack spacing={3}>
                {resultado.canais.map((r) => (
                  <Flex key={`${r.canal}-${r.provedor}`} align="flex-start" gap={2}>
                    <Icon
                      as={r.ok ? MdCheckCircle : MdErrorOutline}
                      color={r.ok ? "green.500" : "red.500"}
                      boxSize={5}
                      mt="1px"
                    />
                    <Box>
                      <Text fontSize="sm" color="gray.800" {...fieldFont}>
                        {CANAL_LABEL[r.canal]}: {r.ok ? `enviado para ${r.destino}` : "não foi enviado"}
                      </Text>
                      {!r.ok && r.motivo && (
                        <Text fontSize="xs" color="red.600" {...fieldFont}>
                          {r.motivo}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                ))}
              </Stack>
              <Text fontSize="sm" color="gray.600" mt={5} {...fieldFont}>
                {algumEnviado
                  ? "Confira se a mensagem chegou como esperado. Se algo não estiver certo, volte e edite o template."
                  : "Nenhum canal foi enviado. Confira o destino e tente de novo, ou edite o template."}
              </Text>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button
                variant="ghost"
                mr="auto"
                onClick={() => setResultado(null)}
                isDisabled={confirmando}
                {...fieldFont}
              >
                Enviar de novo
              </Button>
              <Button
                variant="outline"
                colorScheme="green"
                onClick={onEditar}
                isDisabled={confirmando}
                {...fieldFont}
              >
                Editar template
              </Button>
              <Button
                colorScheme="green"
                onClick={onConfirmar}
                isDisabled={!algumEnviado}
                isLoading={confirmando}
                loadingText="Ativando"
                {...fieldFont}
              >
                {ativaAoConfirmar ? "Confirmar e ativar" : "Confirmar"}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
